import axios from "../../../../../../api/axios";

/**
 * API endpoints for picture operations
 */
export const ENDPOINTS = {
  GET_PICTURES_BY_PROPERTY_ID: "v2/master_sheet/pictures",
  UPLOAD_PICTURE: "v2/master_sheet/pictures/upload",
  ADD_PICTURE_TO_MASTERSHEET: "v2/master_sheet/pictures/add",
  GET_PICTURE_BY_VERSION_NO_FOR_PREVIEW: "v2/master_sheet/pictures/version",
};

/**
 * Gets pictures by property ID, building name, and unit number
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @returns {Promise<Object>} List of pictures with signed URLs
 * @throws {Error} If required parameters are missing or API call fails
 */
export const getPicturesByUnitNo = async (propertyId, buildingName, unitNo) => {
  if (!propertyId || !buildingName || !unitNo) {
    throw new Error('propertyId, buildingName, and unitNo are required');
  }

  try {
    const queryParams = new URLSearchParams({
      property_id: propertyId,
      building_name: buildingName,
      unit_no: unitNo,
    });

    const response = await axios.get(`${ENDPOINTS.GET_PICTURES_BY_PROPERTY_ID}?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pictures:', error);
    throw error;
  }
};

/**
 * Uploads pictures to S3
 * @param {FormData} formData - Form data containing picture files
 * @param {Function} onUploadProgress - Callback for upload progress
 * @returns {Promise<Object>} Upload response data
 * @throws {Error} If API call fails
 */
export const uploadRequestedPictures = async (formData, onUploadProgress) => {
  try {
    const response = await axios.post(ENDPOINTS.UPLOAD_PICTURE, formData, {
      onUploadProgress,
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading pictures:', error);
    throw error;
  }
};

/**
 * Adds pictures to a MasterSheet
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {Object} picture - Picture data to add
 * @returns {Promise<Object>} Added picture response data
 * @throws {Error} If required parameters are missing or API call fails
 */
export const addPicturesToMastersheet = async (propertyId, buildingName, unitNo, picture) => {
  if (!propertyId || !buildingName || !unitNo || !picture) {
    throw new Error('propertyId, buildingName, unitNo, and picture are required');
  }

  try {
    const queryParams = new URLSearchParams({
      property_id: propertyId,
      building_name: buildingName,
      unit_no: unitNo,
    });

    const response = await axios.post(`${ENDPOINTS.ADD_PICTURE_TO_MASTERSHEET}?${queryParams.toString()}`, picture);
    return response.data;
  } catch (error) {
    console.error('Error adding picture to MasterSheet:', error);
    throw error;
  }
};

/**
 * Gets a signed URL for a specific picture version
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {string} pictureType - The picture type (e.g., 'living_room', 'kitchen')
 * @param {number} versionNumber - Version number (0 for current/latest version, positive integer for specific version)
 * @returns {Promise<Object>} Picture version data with signed URL
 * @throws {Error} If required parameters are missing or API call fails
 */
export const getPictureVersionForPreview = async (propertyId, buildingName, unitNo, pictureType, versionNumber) => {
  if (!propertyId || !buildingName || !unitNo || !pictureType) {
    throw new Error('propertyId, buildingName, unitNo, and pictureType are required');
  }

  try {
    const queryParams = new URLSearchParams({
      property_id: propertyId,
      building_name: buildingName,
      unit_no: unitNo,
      picture_type: pictureType,
      version_number: versionNumber.toString(),
    });

    const response = await axios.get(`${ENDPOINTS.GET_PICTURE_BY_VERSION_NO_FOR_PREVIEW}?${queryParams.toString()}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Failed to fetch picture version');
  } catch (error) {
    console.error('Error fetching picture version:', error);
    throw error;
  }
};